import { Button } from "@perceptron/ui/components/button";
import { Icon } from "@perceptron/ui/components/icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@perceptron/ui/components/tooltip";
import { cn } from "@perceptron/utils/cn";

import { useIconHoverPlayback } from "./useIconHoverPlayback";

export interface ClearButtonProps {
  onClear: () => void;
  disabled?: boolean;
  /** Tooltip text, e.g. "Clear Prompt". */
  tooltip: string;
  className?: string;
}

/**
 * Shared clear control: the designed `clear` icon as a ghost button with a tooltip. The icon
 * sweeps while hovered and plays back to its first frame when the pointer leaves. Presentational
 * — each mode owns what "clear" does and (if it wants one) any confirm dialog wrapped around it.
 */
const ClearButton = ({ onClear, disabled, tooltip, className }: ClearButtonProps) => {
  const playback = useIconHoverPlayback("reverse");

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          className={cn("h-auto rounded-full p-3 text-foreground-primary shadow-none label-2", className)}
          data-testid="clear-button"
          disabled={disabled}
          size="sm"
          variant="ghost"
          onClick={onClear}
          onPointerEnter={playback.onPointerEnter}
          onPointerLeave={playback.onPointerLeave}
        >
          <Icon
            animated
            animationRef={playback.animationRef}
            aria-hidden
            autoplay={false}
            className={cn("size-4 transition-opacity", disabled ? "opacity-50" : "opacity-90")}
            loop={false}
            name="clear"
            ref={playback.svgRef}
          />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
};

export default ClearButton;
